import { Component, HostListener, AfterViewInit, OnDestroy } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '../service/layout.service';
import { MenubarModule } from 'primeng/menubar';
import { ThemeService } from '../../services/theme.service';
import { ThemeHelper } from '../../helper/theme.helper';
import { AuthService } from '../../services/auth.service';
import { SessionData } from '../../helper/auth.helper';
import { take, Observable, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { UserMenuDropdown } from '@/shared/user-menu-dropdown/user-menu-dropdown';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

@Component({
    selector: 'app-management-topbar',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, StyleClassModule, MenubarModule, TranslateModule, UserMenuDropdown, ButtonModule, ToggleSwitchModule],
    templateUrl: 'app-management.topbar.html'
})
export class AppManagementTopbar implements AfterViewInit, OnDestroy {
    items!: MenuItem[];
    themeData: ThemeHelper | undefined;
    sessionData: SessionData | undefined;
    checked: boolean = false;
    private resizeSubscription?: Subscription;
    private sessionSubscription?: Subscription;

    resize$ = new Observable<number>(subscriber => {
        const emitWidth = () => {
            subscriber.next(window.innerWidth);
        };

        // Emit initial width
        emitWidth();

        // Create ResizeObserver to watch for viewport changes (including dev tools)
        const resizeObserver = new ResizeObserver(emitWidth);
        resizeObserver.observe(document.body);

        // Also listen to window resize events as fallback
        window.addEventListener('resize', emitWidth);

        // Cleanup
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', emitWidth);
        };
    })

    constructor(
        public layoutService: LayoutService,
        private themeService: ThemeService,
        private authService: AuthService,
    ) {
        this.resizeSubscription = this.resize$.subscribe((width: number) => {
            if (width <= 1100 || width > 1417) {
                this.items = this.buildMenuItems();
            } else {
                this.items = this.buildMenuItemsForTablet();
            }

            const userDropdown = (document.querySelector(".layout-topbar-menu") as HTMLDivElement);
            if (userDropdown && width > 1100) {
                userDropdown.classList.remove("hidden");
            } else if (userDropdown) {
                userDropdown.classList.add("hidden");
            }
        });
        this.sessionSubscription = this.authService.sessionData.subscribe({
            next: (data) => {
                this.sessionData = data;
            }
        });

        this.themeService.themeData.subscribe({
            next: (data) => {
                this.themeData = data;
            }
        });
    }

    ngAfterViewInit(): void {
        const userDropdown = (document.querySelector(".layout-topbar-menu") as HTMLDivElement);
        if (window.innerWidth > 1100) {
            userDropdown.classList.remove("hidden");
        } else {
            userDropdown.classList.add("hidden");
        }
    }

    ngOnDestroy() {
        this.resizeSubscription?.unsubscribe();
        this.sessionSubscription?.unsubscribe();
    }

    private buildMenuItems(): MenuItem[] {

        // Default full menu for non-logistics users
        return [
            {
                label: 'Dashboard',
                icon: 'pi pi-fw pi-home',
                routerLink: '/admin/dashboard'
            },
            {
                label: 'Benutzer',
                icon: 'pi pi-fw pi-users',
                items: [
                    {
                        label: "Benutzer",
                        icon: 'pi pi-fw pi-users',
                        items: [
                            { label: "Alle Benutzer", icon: 'pi pi-fw pi-users', routerLink: '/admin/users/' },
                            { label: "Benutzer erstellen", icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/users/create' }
                        ]
                    },
                    {
                        label: "Rollen",
                        icon: 'pi pi-fw pi-id-card',
                        items: [
                            { label: "Alle Rollen", icon: 'pi pi-fw pi-id-card', routerLink: '/admin/roles/' },
                            { label: "Rolle erstellen", icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/roles/create' }
                        ]
                    }
                ]
            },
            {
                label: "Kunden",
                icon: 'pi pi-fw pi-users',
                items: [
                    { label: "Alle Kunden", icon: 'pi pi-fw pi-users', routerLink: '/admin/customers/' },
                    { label: "Kunde erstellen", icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/customers/create' }
                ]
            },
            {
                label: "Produkte",
                icon: 'pi pi-fw pi-box',
                items: [
                    {
                        label: "Produktvorlagen",
                        icon: 'pi pi-fw pi-file-edit',
                        items: [
                            { label: "Alle Produktvorlagen", icon: 'pi pi-fw pi-list', routerLink: '/admin/product-templates' },
                            { label: "Produktvorlage erstellen", icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/product-templates/create' }
                        ]
                    },
                    {
                        label: "Produkte",
                        icon: 'pi pi-fw pi-box',
                        items: [
                            { label: "Alle Produkte", icon: 'pi pi-fw pi-list', routerLink: '/admin/products/' },
                            { label: "Produkt erstellen", icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/products/create' },
                        ]
                    }
                ]
            },
            {
                label: "Abrechnungen",
                icon: 'pi pi-fw pi-receipt',
                items: [
                    {
                        label: "Entwürfe",
                        icon: 'pi pi-fw pi-inbox',
                        routerLink: '/admin/invoices/outlines',
                    },
                    {
                        label: "Freigegeben",
                        icon: 'pi pi-fw pi-receipt',
                        routerLink: '/admin/invoices/confirmed',
                    }
                ]
            },
            {
                label: 'Freigabeanfragen',
                icon: 'pi pi-fw pi-file-edit',
                routerLink: '/admin/requests-for-approval'
            },
            {
                label: 'Einstellungen',
                icon: 'pi pi-fw pi-cog',
                routerLink: '/admin/settings'
            }
        ];
    }

    private buildMenuItemsForTablet(): MenuItem[] {

        // Default full menu for non-logistics users
        return [
            {
                label: '',
                icon: 'pi pi-fw pi-home',
                routerLink: '/admin/dashboard'
            },
            {
                label: '',
                icon: 'pi pi-fw pi-users',
                items: [
                    {
                        label: "Benutzer",
                        icon: 'pi pi-fw pi-users',
                        items: [
                            { label: "Alle Benutzer", icon: 'pi pi-fw pi-users', routerLink: '/admin/users/' },
                            { label: "Benutzer erstellen", icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/users/create' }
                        ]
                    },
                    {
                        label: "Rollen",
                        icon: 'pi pi-fw pi-id-card',
                        items: [
                            { label: "Alle Rollen", icon: 'pi pi-fw pi-id-card', routerLink: '/admin/roles/' },
                            { label: "Rolle erstellen", icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/roles/create' }
                        ]
                    }
                ]
            },
            {
                label: "",
                icon: 'pi pi-fw pi-users',
                items: [
                    { label: "Alle Kunden", icon: 'pi pi-fw pi-users', routerLink: '/admin/customers/' },
                    { label: "Kunde erstellen", icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/customers/create' }
                ]
            },
            {
                label: "",
                icon: 'pi pi-fw pi-box',
                items: [
                    {
                        label: "Produktvorlagen",
                        icon: 'pi pi-fw pi-file-edit',
                        items: [
                            { label: "Alle Produktvorlagen", icon: 'pi pi-fw pi-list', routerLink: '/admin/product-templates' },
                            { label: "Produktvorlage erstellen", icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/product-templates/create' }
                        ]
                    },
                    {
                        label: "Produkte",
                        icon: 'pi pi-fw pi-box',
                        items: [
                            { label: "Alle Produkte", icon: 'pi pi-fw pi-list', routerLink: '/admin/products/' },
                            { label: "Produkt erstellen", icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/products/create' },
                        ]
                    }
                ]
            },
            {
                label: '',
                icon: 'pi pi-fw pi-pencil',
                routerLink: '/admin/requests-for-approval'
            },
            {
                label: "",
                icon: 'pi pi-fw pi-receipt',
                items: [
                    {
                        label: "Entwürfe",
                        icon: 'pi pi-fw pi-inbox',
                        routerLink: '/admin/invoices/outlines'
                    },
                    {
                        label: "Freigegeben",
                        icon: 'pi pi-fw pi-receipt',
                        routerLink: '/admin/invoices/confirmed'
                    }
                ]
            },
            {
                label: '',
                icon: 'pi pi-fw pi-cog',
                routerLink: '/admin/settings'
            }
        ];
    }
}
